import { useCallback, useEffect, useRef, useState } from 'react';
import ComboBoxFilter from '../../Framework/Components/Forms/ComboBox/Components/ComboBoxFilter';
import { GetObjectIndexByKeyValue } from '../../Framework/Constants/Helpers';
import ComboBoxNoResults from '../../Framework/Components/Forms/ComboBox/Components/ComboBoxNoResults';
import useFilterSelectOptions from '../../Framework/Hooks/useFilterSelectOptions';
import ComboBoxItem from '../../Framework/Components/Forms/ComboBox/Components/ComboBoxItem';
import { useOutsiderDetector } from '../../Framework/Hooks/useOutsiderDetector';
import { CountryCodes } from '../../Framework/Data/CountryCodes';
import CountryCodeFlag from '../../Framework/Components/CountryCodeFlag';

export interface CountryCodeControlType {
	name: string;
	label?: string;
	placeholder: string;

	defaultValue?: any;

	error?: any;

	onChange: any;
	onBlur?: any;

	isLoading?: boolean;
	minimal?: boolean;
	listAlwaysVisible?: boolean;

	displayExtension?: boolean;
}

export interface CountryCodeEntryType {
	label: string;
	value: any;
	icon?: string;
	valueSecondary?: string;
}

const CountryCodeControl = (props: CountryCodeControlType) => {
	const [displayValue, setDisplayValue] = useState<string>('');
	const [isListOpen, setIsListOpen] = useState<boolean>(props.listAlwaysVisible ?? false);
	const [selectedItem, setSelectedItem] = useState<CountryCodeEntryType>({ label: '', value: null });

	const selectRef = useRef<HTMLDivElement>(null);
	const filterRef = useRef<HTMLInputElement>(null);

	const {
		optionsCycled,
		filterSelectValue,
		filterSelectItems,
		FilterSelectList,
		ResetFilterSelectOptions,
		HandleFilterSelectBlur,
		HandleFilterSelectFocus,
	} = useFilterSelectOptions({ filterData: CountryCodes, filterSecondaryValue: true });

	useOutsiderDetector(selectRef, () => (isListOpen ? UpdateDisplayValue() : null));

	const Reset = () => {
		setDisplayValue('');
		setIsListOpen(props.listAlwaysVisible ?? false);
		setSelectedItem({ label: '', value: null });
	};

	const UpdateListVisibility = (state: boolean) => {
		setIsListOpen(props.listAlwaysVisible ?? state);
	};

	const HandleDefaultValue = (value: string) => {
		const itemIndex = GetObjectIndexByKeyValue(CountryCodes, 'valueSecondary', value);

		console.log(itemIndex, value, CountryCodes[itemIndex]);
		if (itemIndex > -1) {
			HandleSelection(null, CountryCodes[itemIndex]);
		} else {
			setSelectedItem({ label: '', value: null });
		}
	};

	const HandleSelection = (e: any, item: any) => {
		if (item.value === 'Clear') {
			console.log(item);
			props.onChange(item);
			Reset();
			return;
		}
		if (!e || !e.code) {
			setSelectedItem(item);
			props.onChange(item);
		} else if ((e.code && e.code === 'ArrowDown') || e.code === 'ArrowUp') {
			//FilterSelectKeyBoardNav(e);
		}
	};

	const UpdateDisplayValue = () => {
		const value = GetObjectIndexByKeyValue(CountryCodes, 'value', selectedItem?.value);
		const existsInData = value > -1;

		setDisplayValue(
			props.isLoading
				? 'Loading...'
				: selectedItem && existsInData
				? `${CountryCodes[value].icon} ${
						props.displayExtension ? CountryCodes[value].valueSecondary : CountryCodes[value].label
				  }`
				: ''
		);
		setIsListOpen(false);
	};

	const UpdateFilterValue = () => {
		if (filterRef.current) {
			filterRef.current.value = isListOpen ? filterSelectValue : displayValue;
		}
	};

	const HandleFilterSelectBlurCallback = useCallback(
		(e: any) => {
			if (isListOpen) {
				UpdateListVisibility(false);
			}

			if (props.onBlur) {
				props.onBlur(e, selectedItem);
			}
		},
		[selectedItem]
	);

	const HandleFilterSelectFocusCallback = () => {
		UpdateListVisibility(true);
	};

	useEffect(() => {
		UpdateDisplayValue();
	}, [props, selectedItem]);

	useEffect(() => {
		UpdateFilterValue();
	}, [displayValue, isListOpen]);

	useEffect(() => {
		if (optionsCycled) {
			UpdateDisplayValue();
		}
	}, [optionsCycled]);

	useEffect(() => {
		if (props.defaultValue) {
			HandleDefaultValue(props.defaultValue);
		}
	}, [props.defaultValue]);

	return (
		<div className={'flex-column multi-select-container flex-gap-lg'} ref={selectRef}>
			<div className={props.listAlwaysVisible && !props.minimal ? 'multi-select-inline-control' : ''}>
				<ComboBoxFilter
					filterRef={filterRef}
					filterSelectValue={filterSelectValue}
					placeholder={props.placeholder ? `Filter ${props.placeholder}...` : 'Filter...'}
					label={props.label ?? ''}
					resetCallback={(e: any) => ResetFilterSelectOptions()}
					blurCallback={(e: any) => HandleFilterSelectBlur(e, HandleFilterSelectBlurCallback)}
					filterActionCallback={(e: any) => FilterSelectList(e)}
					filterNavigationCallback={(e: any) => null} //HandleFilterSelectNavigation(e)}
					filterFocusCallback={(e: any) => HandleFilterSelectFocus(e, HandleFilterSelectFocusCallback)}
					minimal={props.minimal || !props.label}
					errorMessage={props.error?.message}
					isValid={CountryCodes && CountryCodes.length && !props.error ? true : null}
					isActive={isListOpen}
					isLoading={props.isLoading}
				/>

				<div
					className={`${
						props.listAlwaysVisible && !props.minimal
							? 'multi-select-inline-list'
							: 'generic-menu-container'
					} ${props.minimal ? 'in-dropdown-menu' : ''} multi-select-entries ${
						isListOpen ? 'multi-select-active' : ''
					}`}
					style={{ maxHeight: '300px', overflow: 'auto' }}
					tabIndex={-1}
				>
					{filterSelectItems && filterSelectItems.length === 0 && <ComboBoxNoResults />}

					{filterSelectItems &&
						filterSelectItems.length > 0 &&
						filterSelectItems.map((item, index: number) => {
							if (item.value === undefined || item.value === '') return null;

							return (
								<ComboBoxItem
									key={`comboItem${props.name}${item.value}${index}`}
									name={props.name}
									label={props.name}
									itemLabel={
										<>
											{item.label === 'Clear' ? (
												<div />
											) : (
												<CountryCodeFlag countryCode={item.value} title={item.label} />
											)}
											{props.displayExtension ? item.valueSecondary : item.label}
										</>
									}
									item={item}
									selectedValues={selectedItem.value}
									onChangeCallback={(e: any, item: any) => HandleSelection(e, item)}
									onKeyDownCallback={(e: any) => null} //FilterSelectKeyBoardNav(e)}
								/>
							);
						})}
				</div>
			</div>
		</div>
	);
};

export default CountryCodeControl;
